import Button from '@ingka/button';
import Search from '@ingka/search';
import Text from '@ingka/text';
import type React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import '@ingka/search/dist/style.css';
import SSRIcon from '@ingka/ssr-icon';
import arrowLeftPath from '@ingka/ssr-icon/paths/arrow-left';
import arrowRightPath from '@ingka/ssr-icon/paths/arrow-right';
import databasePath from '@ingka/ssr-icon/paths/database';
import { useApp } from '@/context/AppContext';
import { useProductName } from '@/hooks/useCatalogNames';
import styles from './EntityStep.module.css';

export default function EntityStep() {
  const { catalog, product, setEntity } = useApp();
  const navigate = useNavigate();
  const productLabel = useProductName(product);

  const list = catalog?.products.find((p) => p.id === product)?.entities ?? [];
  const [q, setQ] = useState('');
  const filtered = q.trim()
    ? list.filter(
        (e) =>
          e.name.toLowerCase().includes(q.toLowerCase()) ||
          (e.description ?? '').toLowerCase().includes(q.toLowerCase()),
      )
    : list;

  const pick = (entityId: string) => {
    setEntity(entityId);
    navigate('/upload/classification');
  };

  const goBack = () => {
    setEntity(null);
    navigate('/upload/product');
  };

  return (
    <div className={styles.page}>
      <Button
        type="tertiary"
        size="small"
        text="Back to product"
        ssrIcon={arrowLeftPath}
        onClick={goBack}
        className={styles.backBtn}
      />

      <div className={styles.hero}>
        <div className={styles.productBadge}>{productLabel}</div>
        <Text tagName="h2" textStyle="Heading.XL" className={styles.heroTitle}>
          What kind of Entity is in the file?
        </Text>
        <Text tagName="p" textStyle="Body.L" className={styles.heroBody}>
          Select the entity type that best describes the data in the file.
        </Text>
      </div>

      <div className={styles.searchBar}>
        <div className={styles.searchWrap}>
          <Search
            id="entity-search"
            placeholder="Search entities…"
            value={q}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQ(e.target.value)}
            onClear={() => setQ('')}
            focusWithClearButton
            ariaLabel="Search entities"
          />
        </div>
        {q && filtered.length === 0 && (
          <p className={styles.searchEmpty}>No entities match &ldquo;{q}&rdquo;</p>
        )}
      </div>

      <div className={styles.grid}>
        {filtered.map((e) => (
          <button key={e.id} type="button" onClick={() => pick(e.id)} className={styles.card}>
            <div className={styles.cardTop}>
              <div className={styles.cardIcon}>
                <SSRIcon paths={databasePath} />
              </div>
              <SSRIcon paths={arrowRightPath} className={styles.cardArrow} />
            </div>
            <Text tagName="h3" textStyle="Heading.L" className={styles.cardName}>
              {e.name}
            </Text>
            <Text tagName="p" textStyle="Body.S" className={styles.cardDesc}>
              {e.description ?? ''}
            </Text>
          </button>
        ))}
      </div>
    </div>
  );
}
